import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import prettyBtyes from 'pretty-bytes';
import { withNamespaces } from 'react-i18next';
import { ErrorMessage } from 'formik';
import LabelR from '../../Simple/LabelR';
import { fileNameFormatter } from '../../../utils/Formatters/ListFormatters';

const DropZone = (props) => {
  const {
    t,
    field: { name },
    form: { touched, errors, setFieldValue, setFieldTouched },
    required,
    label,
    allowedExt,
    maxSize = 4194304,
    minSize,
    multiple = false,
    values,
    instructions,
  } = props;
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      let value = [];
      acceptedFiles.forEach((file) => {
        const blob = new Blob([file], { type: 'text/csv' });
        const newFile = blobToFile(blob, file.name);
        const reader = new FileReader();
        reader.onloadend = () => {
          value = [...value, { title: newFile.name, file: reader.result }];
          if (acceptedFiles.length === 1) {
            setFieldValue(name, value[0]);
          } else {
            setFieldValue(name, value);
          }
        };
        reader.readAsDataURL(newFile);
      });
    } else {
      setFieldValue(name, '');
      setError('');
    }
  }, [acceptedFiles, values]);

  const blobToFile = (blobFile, fileName) => {
    /* eslint-disable no-param-reassign */
    blobFile.lastModifiedDate = new Date();
    blobFile.name = fileName;
    /* eslint-disable no-param-reassign */
    return blobFile;
  };

  const onDropAccepted = useCallback((files) => {
    setAcceptedFiles([...acceptedFiles, ...files]);
  }, []);

  const removeFile = (file) => () => {
    const newFiles = [...acceptedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setAcceptedFiles(newFiles);
    setFieldTouched(name);
  };

  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      setError('');
      fileRejections.forEach((file) => {
        if (file.size > maxSize) {
          setError(t('labelMaxSize', { size: prettyBtyes(maxSize) }));
        }
      });
    },
    onDropAccepted,
    accept: allowedExt,
    multiple,
    minSize,
    maxSize,
  });

  const classes = classNames('drop-zone', {
    'is-invalid': (errors[name] && touched[name]) || isDragReject,
  });

  const classesContainer = classNames('drop-zone__container', {
    'drop-zone--selected': acceptedFiles.length > 0,
  });

  const classesLabel = classNames('drop-zone__label', {
    'with-border': acceptedFiles.length > 0,
  });

  const classesInstructions = classNames('drop-zone__instructions');

  const renderInstructions = () => {
    const { instructions } = props;
    return (
      <ul>
        {instructions.map((instruction) => (
          <li key={instruction.id}>{instruction.text}</li>
        ))}
      </ul>
    );
  };

  return (
    <div className="form-group">
      {label && (required ? <LabelR htmlFor={name}>{label}</LabelR> : <label htmlFor={name}>{label}</label>)}
      <div className={classes}>
        {instructions && <div className={classesInstructions}>{renderInstructions()}</div>}
        <div className={classesContainer}>
          <div {...getRootProps({ className: 'drop-zone__input' })}>
            <input {...getInputProps()} />
            <FontAwesomeIcon className="drop-zone__icon" icon={{ prefix: 'fal', iconName: 'cloud-upload' }} />
            <span className={classesLabel}>
              {t('labelDropZone')} ({allowedExt})
            </span>
          </div>
          <div className="drop-zone__files">
            {acceptedFiles.map((file) => (
              <div className="drop-zone__files__content" key={file.path}>
                <span>
                  <span>
                    {fileNameFormatter(file.path)}
                    <span className="size">({prettyBtyes(file.size)})</span>
                  </span>
                </span>
                <FontAwesomeIcon className="drop-zone__remove" icon={{ prefix: 'fal', iconName: 'trash-alt' }} onClick={removeFile(file)} />
              </div>
            ))}
          </div>
          <span className="drop-zone__rules">[{t('labelMaxSize', { size: prettyBtyes(maxSize) })}]</span>
        </div>
      </div>
      {touched[name] && errors[name] && <ErrorMessage name={name} component="div" className="invalid-feedback" />}
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};
export default withNamespaces()(DropZone);
